import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  createAdminTerminationConfirmationDelivery,
  fetchAdminTerminationConfirmationEmailPreview,
} from "../../../../api/adminData";
import { SECONDARY_BUTTON } from "../../tenancies/workspaceUi";
import TerminationConfirmationSendDialog from "./TerminationConfirmationSendDialog";
import {
  ALREADY_SENT_TEXT,
  PREVIEW_UNREADABLE,
  SENDING_EXPLANATION,
  SENT_TEXT,
  STALE_NOTICE,
  deliveryAction,
  deliveryErrorText,
  deliveryHeadline,
  deliveryState,
  dialogModeFor,
  earlierSentText,
  failureReasonText,
  isReadablePreview,
  latestDelivery,
  previewErrorText,
  recipientText,
} from "./terminationConfirmationDeliveryModel";

const LINE = "m-0 min-w-0 break-all text-[12px] leading-snug text-muted-foreground";
const NOTE = "m-0 text-[12.5px] leading-snug text-foreground";

/**
 * KND-4b — the e-mail part of the current confirmation version, inside the
 * KND-3c panel (never a second workspace).
 *
 * THE SERVER DECIDES. The version's delivery history comes with the panel's
 * confirmations GET; whether a send is possible right now comes from the
 * preview; the send itself re-checks every rule. Nothing here derives a
 * recipient, a sender, a subject or a body.
 *
 * REQUESTS. Nothing on mount — the history is already in the panel's answer.
 * A click on "Per E-Mail senden" / "Erneut senden" asks for the preview once
 * and opens the dialog; "Jetzt senden" is one POST, and whatever it answers,
 * `onRefresh` — the panel's one confirmations GET — follows exactly once. No
 * retry, no polling, no page reload.
 *
 * PESSIMISTIC. The button is disabled while the preview loads and the dialog
 * cannot be dismissed while the POST runs; a ref stops a second request even
 * within the same tick. A resend names the latest attempt this client knows
 * (`previous_delivery_id`), so a state that moved meanwhile is a 409, not a
 * second mail.
 */
export default function TerminationConfirmationDelivery({
  version,
  stale = false,
  canAct = true,
  onRefresh,
}) {
  const [previewing, setPreviewing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [dialog, setDialog] = useState(null); // { mode, preview } | null

  const busyRef = useRef(false);
  const statusRef = useRef(null);
  const triggerRef = useRef(null);
  const focusStatusRef = useRef(false);

  // A request that answers after the panel was closed must not set state.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  if (!version?.id) return null;

  const state = deliveryState(version);
  const latest = latestDelivery(version);
  const action = deliveryAction({
    view: stale ? "stale" : "current",
    state,
    canAct,
  });
  const headingId = `termination-confirmation-delivery-${version.id}`;

  const handleOpen = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    const mode = dialogModeFor(state);
    setPreviewing(true);
    try {
      const preview = await fetchAdminTerminationConfirmationEmailPreview(version.id);
      if (!mountedRef.current) return;
      if (!isReadablePreview(preview, version.id)) {
        toast.error(PREVIEW_UNREADABLE);
        return;
      }
      setDialog({ mode, preview });
    } catch (err) {
      if (mountedRef.current) toast.error(previewErrorText(err));
    } finally {
      busyRef.current = false;
      if (mountedRef.current) setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    if (busyRef.current) return;
    // The dialog shows the preview of ONE version; if the panel has moved on
    // to another version meanwhile, nothing is sent for either.
    if (dialog?.preview?.confirmation_id !== version.id) {
      setDialog(null);
      return;
    }
    busyRef.current = true;
    setPosting(true);
    // The latest attempt THIS client knows: a resend the server no longer
    // expects is refused instead of becoming a second mail.
    const previous = latestDelivery(version);
    try {
      const result = await createAdminTerminationConfirmationDelivery(version.id, {
        previousDeliveryId: previous ? previous.id : null,
      });
      if (mountedRef.current) {
        toast.success(result?.outcome === "already_sent" ? ALREADY_SENT_TEXT : SENT_TEXT);
      }
    } catch (err) {
      if (mountedRef.current) toast.error(deliveryErrorText(err));
    }
    if (!mountedRef.current) return;
    // Sent, refused or unknown: the history moved or has to be read again.
    // One GET, never a retry.
    focusStatusRef.current = true;
    setDialog(null);
    setPosting(false);
    try {
      await onRefresh?.();
    } finally {
      busyRef.current = false;
    }
  };

  const failedReason = state === "failed" ? failureReasonText(latest?.error_code) : null;
  const earlier = earlierSentText(version);
  const recipient = recipientText(latest);
  const buttonLabel = action === "resend" ? "Erneut senden" : "Per E-Mail senden";

  return (
    <div
      role="group"
      aria-labelledby={headingId}
      className="flex w-full min-w-0 flex-col items-start gap-1"
      data-testid="termination-confirmation-delivery"
    >
      <p id={headingId} className="m-0 text-[11.5px] leading-tight text-muted-foreground">
        E-Mail-Versand
      </p>
      <p
        ref={statusRef}
        tabIndex={-1}
        aria-live="polite"
        className="m-0 text-[12.5px] font-medium text-foreground outline-none"
        data-testid="termination-confirmation-delivery-status"
      >
        {deliveryHeadline(version)}
      </p>
      {recipient ? <p className={LINE}>{recipient}</p> : null}
      {failedReason ? <p className={LINE}>{failedReason}</p> : null}
      {earlier ? <p className={LINE}>{earlier}</p> : null}
      {state === "sending" ? <p className={NOTE}>{SENDING_EXPLANATION}</p> : null}
      {stale ? <p className={NOTE}>{STALE_NOTICE}</p> : null}
      {action ? (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <button
            ref={triggerRef}
            type="button"
            className={SECONDARY_BUTTON}
            onClick={handleOpen}
            disabled={previewing || posting}
            aria-busy={previewing}
            aria-label={`${buttonLabel}, Version ${version.version}`}
          >
            {buttonLabel}
          </button>
        </div>
      ) : null}
      <TerminationConfirmationSendDialog
        open={Boolean(dialog)}
        mode={dialog?.mode}
        preview={dialog?.preview}
        busy={posting}
        onOpenChange={(next) => {
          if (!next) setDialog(null);
        }}
        onConfirm={handleConfirm}
        onCloseAutoFocus={(event) => {
          // Set by hand both ways: the button was disabled while the preview
          // loaded, so the browser may have dropped its focus before the
          // dialog opened. After a send the button may be gone and the status
          // line carries the outcome; a cancel returns to the button.
          event.preventDefault();
          if (focusStatusRef.current) {
            focusStatusRef.current = false;
            statusRef.current?.focus();
          } else {
            triggerRef.current?.focus();
          }
        }}
      />
    </div>
  );
}
